import { useSignUp } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormCheckbox } from '@/components/common/form-inputs/FormCheckbox';
import { FormInput } from '@/components/common/form-inputs/FormInput';
import { SignUpFormSchema } from '@/components/forms/schemas/SignUpFormSchema';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { SIGN_UP_ERROR_MESSAGE } from '@/constants/strings';
import { toast } from '@/hooks/ui/use-toast';

export interface SignUpFormProps {
  customSubmit?: (values: z.infer<typeof SignUpFormSchema>) => Promise<void>;
  defaultEmail?: string;
}

export const SignUpForm = ({ customSubmit, defaultEmail }: SignUpFormProps) => {
  const form = useForm<z.infer<typeof SignUpFormSchema>>({
    resolver: zodResolver(SignUpFormSchema),
    defaultValues: {
      email: defaultEmail || '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [expired, setExpired] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { isLoaded, signUp } = useSignUp();

  const password = form.watch('password');
  const confirmPassword = form.watch('confirmPassword');

  const passwordRequirements = {
    minLength: password?.length >= 8,
    hasUppercase: /[A-Z]/.test(password || ''),
    hasLowercase: /[a-z]/.test(password || ''),
    hasNumber: /[0-9]/.test(password || ''),
    hasSpecialChar: /[^A-Za-z0-9]/.test(password || ''),
    passwordsMatch: password === confirmPassword && password?.length > 0,
  };

  const allRequirementsMet =
    passwordRequirements.minLength &&
    passwordRequirements.hasUppercase &&
    passwordRequirements.hasLowercase &&
    passwordRequirements.hasNumber &&
    passwordRequirements.hasSpecialChar &&
    passwordRequirements.passwordsMatch;

  const onSubmit = async (values: z.infer<typeof SignUpFormSchema>) => {
    if (!isLoaded) {
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'Please try again later',
      });
      return;
    }

    if (customSubmit) {
      setLoading(true);
      await customSubmit(values);
      return;
    }

    const { startEmailLinkFlow } = signUp.createEmailLinkFlow();

    setLoading(true);
    try {
      await signUp?.create({
        emailAddress: values.email,
        password: values.password,
      });

      toast({
        title: 'Email verification link has been sent',
        description: 'Please check your email',
      });

      const response = await startEmailLinkFlow({
        redirectUrl: `${window.location.origin}/${window.localStorage.getItem('active-org-id')}/auth/confirm`,
      });

      const verification = response.verifications.emailAddress;

      if (verification.status === 'expired') {
        setExpired(true);
        setLoading(false);
        return;
      }

      if (response.status === 'complete') {
        setLoading(false);
        toast({
          title: 'Email verification complete',
          description: 'You can now sign in with your account',
        });
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Sign Up Failed',
        description: error?.errors?.[0]?.message || SIGN_UP_ERROR_MESSAGE,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expired) {
      toast({
        variant: 'destructive',
        title: 'Email link has been expired',
        description: 'Please try again',
      });
    }
  }, [expired]);

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-xs">
      {met ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-500" />
      )}
      <span
        className={met ? 'text-xs text-green-600' : 'text-xs text-gray-600'}
      >
        {text}
      </span>
    </div>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-4">
        <FormInput
          control={form.control}
          name="email"
          type="email"
          label="Email"
          placeholder="Email"
          readOnly={!!defaultEmail}
        />
        <div className="relative">
          <FormInput
            control={form.control}
            name="password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            placeholder="Password"
            className="pr-9"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[2.65rem] text-gray-500 hover:text-gray-700"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className="relative">
          <FormInput
            control={form.control}
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirm Password"
            placeholder="Confirm Password"
            className="pr-9"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-[2.65rem] text-gray-500 hover:text-gray-700"
            tabIndex={-1}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        {password && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Password Requirements:
            </p>
            <RequirementItem
              met={passwordRequirements.minLength}
              text="At least 8 characters"
            />
            <RequirementItem
              met={passwordRequirements.hasUppercase}
              text="At least one uppercase letter"
            />
            <RequirementItem
              met={passwordRequirements.hasLowercase}
              text="At least one lowercase letter"
            />
            <RequirementItem
              met={passwordRequirements.hasNumber}
              text="At least one number"
            />
            <RequirementItem
              met={passwordRequirements.hasSpecialChar}
              text="At least one special character"
            />
          </div>
        )}

        {confirmPassword && (
          <RequirementItem
            met={passwordRequirements.passwordsMatch}
            text="Passwords match"
          />
        )}

        <FormCheckbox
          control={form.control}
          name="terms"
          label="I agree to the Terms of Use and Privacy Policy"
        />
        <Button
          type="submit"
          className="w-full"
          disabled={loading || !allRequirementsMet || !form.getValues('terms')}
        >
          {loading ? <Loader2 className="animate-spin" /> : 'Sign Up'}
        </Button>
      </form>
    </Form>
  );
};
